import { useMutation, useQueryClient } from '@tanstack/react-query'
import { communityPostKeys, communityReportKeys } from 'modules/community/api/key-factory'
import * as postsApi from 'modules/community/api/posts-api'
import type { PostInterface } from 'modules/community/types/post-interface'

export const useDeletePost = (): ((postId: number) => Promise<void>) => {
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: async (postId: number): Promise<void> => {
      await postsApi.deletePost(postId)
    },

    onMutate: async postId => {
      await queryClient.cancelQueries({ queryKey: communityPostKeys.all })

      const snapshot = queryClient.getQueriesData({ queryKey: communityPostKeys.all })

      const removePost = (items: PostInterface[]): PostInterface[] =>
        items.filter(p => p.id !== postId)

      queryClient.setQueriesData<PostInterface[]>(
        { queryKey: communityPostKeys.pinnedLists() },
        old => (old ? removePost(old) : old),
      )
      queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
        { queryKey: communityPostKeys.all },
        old => {
          if (!old || !('pages' in old)) return old
          return {
            ...old,
            pages: old.pages.map(page => ({ ...page, items: removePost(page.items) })),
          }
        },
      )

      return { snapshot }
    },

    onError: (_err, _postId, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
      queryClient.invalidateQueries({ queryKey: communityReportKeys.all })
    },
  })

  return mutateAsync
}
